import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// ============================================================
// MarketingHeader - ported from novala-marketing/Header.tsx
// Matches marketing site design exactly. Uses react-i18next.
// Background #0A0A0A, 72px height, 6 nav items, mega menus.
// ============================================================

const MENUS = {
  productsFeatures: {
    columns: [
      { labelKey: 'products', linkKeys: ['accounting','payroll','invoicing','timeTracking','businessManagement'] },
      { labelKey: 'features', linkKeys: ['bookkeeping','getInsights','planAhead','expenseTracking','manageBills','receiptCapture','reporting','cashFlow','manageCustomers','directDeposit','vela'], seeAllKey: 'seeAllFeatures' },
      { labelKey: 'businessStage', linkKeys: ['newBusiness','smallBusiness','midSized','freelance'] },
      { labelKey: 'industry', linkKeys: ['construction','professional','wholesale','healthcare','retail','restaurants','nonprofit'], seeAllKey: 'seeAllIndustries' },
    ],
  },
  solutions: {
    columns: [
      { labelKey: 'byBusinessSize', linkKeys: ['smallBusinesses','growingTeams'] },
      { labelKey: 'byBusinessNeed', linkKeys: ['payroll','accounting','invoicing','expenses','reports','vela'] },
    ],
  },
  industries: {
    columns: [
      { labelKey: 'primary', linkKeys: ['construction','healthcare','hospitality','professionalServices'] },
      { labelKey: 'secondary', linkKeys: ['retail','nonprofits','transportation','foodBeverage'], seeAllKey: 'allIndustries' },
    ],
  },
  resources: {
    columns: [
      { labelKey: 'compare', linkKeys: ['vsQuickbooks','vsXero','vsWave'] },
      { labelKey: 'exploreNovala', linkKeys: ['whatsNew','moveToNovala','appsIntegrations'] },
      { labelKey: 'freeTools', linkKeys: ['invoiceGen','payrollCalc','timesheetCalc','invoiceTemplates','employeeCostCalc'], seeAllKey: 'seeAllTools' },
      { labelKey: 'learnSupport', linkKeys: ['blog','guides','tutorials','productSupport'] },
    ],
  },
};

const NAV_ITEMS = [
  { key: 'productsFeatures', menuKey: 'productsFeatures' },
  { key: 'plansPricing', href: 'https://www.getnovala.com/pricing' },
  { key: 'resources', menuKey: 'resources' },
  { key: 'solutions', menuKey: 'solutions' },
  { key: 'industries', menuKey: 'industries' },
  { key: 'accountants', href: 'https://www.getnovala.com/#accountants' },
];

const SIGNIN_ITEMS = ['novala','accountantPortal','employeeApp'];
const SIGNIN_ROUTES = { novala: '/login', accountantPortal: '/login', employeeApp: '/employee-portal' };

export default function MarketingHeader() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const signInCloseTimer = useRef(null);

  useEffect(() => {
    const onEscape = (e) => { if (e.key === 'Escape') { setOpenMenu(null); setSignInOpen(false); } };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, []);

  const currentLang = i18n.language && i18n.language.startsWith('fr') ? 'fr' : 'en';
  const toggleLang = () => { i18n.changeLanguage(currentLang === 'en' ? 'fr' : 'en'); };

  const handleTriggerEnter = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => setOpenMenu(key), 100);
  };
  const handleTriggerLeave = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 200);
  };
  const handlePanelEnter = () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
  const handlePanelLeave = () => { closeTimer.current = setTimeout(() => setOpenMenu(null), 200); };
  const handleSignInEnter = () => { if (signInCloseTimer.current) clearTimeout(signInCloseTimer.current); setSignInOpen(true); };
  const handleSignInLeave = () => { signInCloseTimer.current = setTimeout(() => setSignInOpen(false), 200); };

  const handleNavClick = (item) => {
    if (item.menuKey) { setOpenMenu(openMenu === item.menuKey ? null : item.menuKey); return; }
    if (item.href) { window.location.href = item.href; }
  };

  const goPricing = () => { window.location.href = 'https://www.getnovala.com/pricing'; };
  const goLogin = () => navigate('/login');

  const VelaPromoCard = () => (
    <div style={{ flexShrink: 0, width: 380 }}>
      <div style={{ padding: 32, position: 'relative', background: 'linear-gradient(#0E2A2A,#0E2A2A) padding-box, linear-gradient(135deg,#00A651,#4EDDA0) border-box', border: '2px solid transparent', borderRadius: 16 }}>
        <div style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16, background: '#F4FBF7', color: '#023E22' }}>{t('header.promoCard.newLabel')}</div>
        <h3 style={{ color: 'white', fontSize: 28, fontWeight: 400, margin: '0 0 12px 0', lineHeight: 1.2 }}>{t('header.promoCard.heading')}</h3>
        <p style={{ color: 'white', fontSize: 15, lineHeight: 1.5, marginBottom: 24 }}>{t('header.promoCard.body')}</p>
        <a href='#vela' onClick={() => setOpenMenu(null)} style={{ display: 'inline-block', background: '#00A651', color: 'white', fontWeight: 700, padding: '12px 28px', borderRadius: 8, textDecoration: 'none' }}>{t('header.promoCard.cta')} →</a>
      </div>
    </div>
  );

  return (
    <header style={{ position: 'sticky', top: 0, left: 0, right: 0, zIndex: 50, height: 72, background: '#0A0A0A' }}>
      <div style={{ width: '100%', height: '100%', padding: '0 40px', display: 'flex', alignItems: 'center' }}>
        <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', flexShrink: 0, cursor: 'pointer' }}>
          <img src='/logo-full-green.svg' alt={t('header.logoAlt')} style={{ height: 48, width: 'auto', display: 'block' }} />
        </div>

        <nav style={{ display: 'flex', gap: 44, marginLeft: 176, alignItems: 'center' }} aria-label='Primary'>
          {NAV_ITEMS.map((item) => (
            item.menuKey ? (
              <div key={item.key} style={{ position: 'relative' }} onMouseEnter={() => handleTriggerEnter(item.menuKey)} onMouseLeave={handleTriggerLeave}>
                <button onClick={() => handleNavClick(item)} style={{ background: 'transparent', border: 'none', padding: '8px 0', fontSize: 16, fontWeight: openMenu === item.menuKey ? 600 : 400, color: openMenu === item.menuKey ? 'white' : 'rgba(255,255,255,0.95)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {t('header.nav.' + item.key)}
                  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' stroke='currentColor' strokeWidth='2' style={{ transform: openMenu === item.menuKey ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}><polyline points='3 5 7 9 11 5'/></svg>
                </button>
                {openMenu === item.menuKey && <div style={{ position: 'absolute', left: 0, right: 0, height: 2, bottom: -4, background: '#F4FBF7' }} />}
              </div>
            ) : (
              <a key={item.key} href={item.href} style={{ fontSize: 16, color: 'rgba(255,255,255,0.95)', textDecoration: 'none', padding: '8px 0' }}>{t('header.nav.' + item.key)}</a>
            )
          ))}
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginLeft: 'auto', flexShrink: 0 }}>
          <a href='#talk-to-sales' style={{ fontSize: 16, color: 'rgba(255,255,255,0.95)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {t('header.utility.talkToSales')}
            <svg width='10' height='10' viewBox='0 0 10 10' fill='none' stroke='currentColor' strokeWidth='2'><polyline points='2 4 5 7 8 4'/></svg>
          </a>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>|</span>
          <button onClick={toggleLang} style={{ background: 'transparent', border: 'none', fontSize: 16, color: 'white', fontWeight: 600, cursor: 'pointer' }}>{currentLang === 'en' ? 'FR' : 'EN'}</button>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>|</span>
          <div style={{ position: 'relative' }} onMouseEnter={handleSignInEnter} onMouseLeave={handleSignInLeave}>
            <button onClick={() => setSignInOpen(!signInOpen)} style={{ background: 'transparent', border: 'none', fontSize: 16, color: 'rgba(255,255,255,0.95)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {t('header.utility.signIn')}
              <svg width='10' height='10' viewBox='0 0 10 10' fill='none' stroke='currentColor' strokeWidth='2' style={{ transform: signInOpen ? 'rotate(180deg)' : 'rotate(0)' }}><polyline points='2 4 5 7 8 4'/></svg>
            </button>
            {signInOpen && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: 'white', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', borderRadius: 8, padding: '12px 0', minWidth: 280 }}>
                {SIGNIN_ITEMS.map((key) => (
                  <a key={key} onClick={() => navigate(SIGNIN_ROUTES[key])} style={{ display: 'block', padding: '10px 20px', fontSize: 15, color: '#161616', textDecoration: 'none', cursor: 'pointer' }}>{t('header.signInMenu.' + key)}</a>
                ))}
                <div style={{ borderTop: '1px solid #E5E7EB', margin: '8px 0' }} />
                <a onClick={() => setSignInOpen(false)} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px', fontSize: 15, color: '#161616', textDecoration: 'none', cursor: 'pointer' }}>{t('header.signInMenu.needHelp')}</a>
              </div>
            )}
          </div>
          <a onClick={goPricing} style={{ background: '#00A651', color: 'white', padding: '10px 20px', borderRadius: 8, fontSize: 16, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}>{t('header.utility.getStarted')}</a>
        </div>
      </div>

      {openMenu && MENUS[openMenu] && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }} onMouseEnter={handlePanelEnter} onMouseLeave={handlePanelLeave}>
          <div style={{ maxWidth: 1600, margin: '0 auto', padding: '40px 64px', display: 'flex', justifyContent: 'space-between', gap: 32 }}>
            <div style={{ display: 'flex', gap: 20 }}>
              {MENUS[openMenu].columns.map((col) => (
                <div key={col.labelKey} style={{ width: 260 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 400, color: '#6B6B6B', marginBottom: 14 }}>{t('header.megaMenu.' + openMenu + '.columns.' + col.labelKey + '.label')}</h3>
                  <div style={{ borderTop: '1px solid #D9D9D9', marginBottom: 24 }} />
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {col.linkKeys.map((linkKey) => (
                      <li key={linkKey}>
                        <a href='#' onClick={(e) => { e.preventDefault(); setOpenMenu(null); }} style={{ fontSize: 16, color: '#161616', textDecoration: 'none' }}>{t('header.megaMenu.' + openMenu + '.columns.' + col.labelKey + '.links.' + linkKey)}</a>
                      </li>
                    ))}
                  </ul>
                  {col.seeAllKey && (
                    <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #D9D9D9' }}>
                      <a href='#' onClick={(e) => { e.preventDefault(); setOpenMenu(null); }} style={{ fontSize: 16, color: '#161616', textDecoration: 'none' }}>{t('header.megaMenu.' + openMenu + '.columns.' + col.labelKey + '.links.' + col.seeAllKey)}</a>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <VelaPromoCard />
          </div>
        </div>
      )}
    </header>
  );
}
